"use client";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import {
  BadgeCheck,
  ArrowRight,
  BookOpen,
  CheckCheck,
  Clock3,
  Compass,
  Flag,
  FlaskConical,
  Gauge,
  KeyRound,
  Languages,
  Loader2,
  LogOut,
  MonitorSmartphone,
  Play,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  UsersRound,
} from "lucide-react";
import { useAuth } from "@/lib/auth/provider";
import { useApi } from "@/lib/hooks/use-api";
import { api, post } from "@/lib/api/client";
import type {
  Analytics,
  DashboardData,
  HistoryItem,
  PageData,
  Session,
  StartSessionResponse,
} from "@/lib/types";
import { date, hoursLabel, minutes } from "@/lib/format";
import { useI18n, useT } from "@/lib/i18n";
import {
  Badge,
  Button,
  DataState,
  Empty,
  PageHeading,
  Pagination,
  Progress,
  SectionTitle,
  Stat,
} from "@/components/ui";
import { DirectionCards, ScenarioCard } from "@/components/catalog/catalog";
import { RequireAuth } from "@/components/auth";
interface CyberScoreDim {
  score: number | null;
  weight: number;
  status: "SCORED" | "NOT_MEASURED";
}
interface CyberScoreData {
  overall: number | null;
  level: string;
  measured_dimensions: number;
  dimensions: Record<string, CyberScoreDim>;
  recommendations: string[];
}
const CYBERSCORE_DIMS = [
  "identity",
  "phishing",
  "device",
  "data_backup",
  "fraud",
  "family_iot",
] as const;
function CyberScoreCard() {
  const auth = useAuth();
  const t = useT();
  const req = useApi<CyberScoreData>(auth.user ? "/cyberscore" : null);
  const d = req.data;
  return (
    <section className="section">
      <div className="panel cyberscore-panel">
        <div className="cyberscore-head">
          <div>
            <div className="eyebrow">{t("cyberscore.eyebrow")}</div>
            <h2 className="panel-title">
              {t("cyberscore.title")}
            </h2>
            <p className="panel-sub">
              {auth.user ? t("cyberscore.desc") : t("cyberscore.guest")}
            </p>
          </div>
          <div className="cyberscore-total">
            <div className="cyberscore-value">
              {d && d.overall != null ? d.overall : "—"}
              <span className="cyberscore-max">/100</span>
            </div>
            <div className={`cyberscore-level level-${(d?.level ?? "NOT_MEASURED").toLowerCase()}`}>
              {d ? t(`cyberscore.level_${d.level}`) : t("cyberscore.level_NOT_MEASURED")}
            </div>
          </div>
        </div>
        <div className="cyberscore-dims">
          {CYBERSCORE_DIMS.map((k) => {
            const dim = d?.dimensions?.[k];
            const val = dim?.score ?? null;
            return (
              <div key={k} className="cyberscore-dim">
                <div className="cyberscore-dim-top">
                  <span className="cyberscore-dim-name">{t(`cyberscore.dim_${k}`)}</span>
                  <span className={val == null ? "cyberscore-dim-val none" : "cyberscore-dim-val"}>
                    {val == null ? t("cyberscore.not_measured") : `${val}`}
                  </span>
                </div>
                <div className="cs-bar">
                  <div className="cs-bar-fill" style={{ width: `${val ?? 0}%` }} />
                </div>
              </div>
            );
          })}
        </div>
        {d && d.recommendations.length > 0 ? (
          <div className="cyberscore-recs">
            {d.recommendations.map((r, i) => (
              <div key={i} className="cyberscore-rec">
                <Sparkles size={14} />
                <span>{r}</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}
export function HistoryTable({ items }: { items: HistoryItem[] }) {
  const t = useT();
  return items.length ? (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>{t("hist.practice")}</th>
            <th>{t("common.result")}</th>
            <th>{t("common.date")}</th>
            <th>{t("common.status")}</th>
            <th>
              <span className="sr-only">{t("common.open")}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.session_id}>
              <td>
                <strong>{item.scenario_title}</strong>
              </td>
              <td>{item.score ?? "—"}</td>
              <td>{date(item.completed_at)}</td>
              <td>
                <Badge value={item.status} />
              </td>
              <td>
                <Link
                  href={`/practice/${encodeURIComponent(item.session_id)}/${["COMPLETED", "PASSED", "FAILED"].includes(item.status) ? "result" : "workspace"}`}
                  aria-label={`${t("common.open")}: ${item.scenario_title}`}
                >
                  <ArrowRight size={17} />
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : (
    <Empty
      title={t("hist.empty_title")}
      text={t("hist.empty_text")}
      action={
        <Link href="/practices" className="text-link">
          {t("hist.choose")}
          <ArrowRight size={16} />
        </Link>
      }
    />
  );
}
function LabQueueCard() {
  const t = useT();
  const auth = useAuth();
  const req = useApi<{
    active: number;
    max: number;
    available: boolean;
    next_free_in_min: number | null;
  }>(auth.user ? "/labs/queue" : null);
  const d = req.data;
  if (!auth.user) return null;
  return (
    <div className={`lab-queue ${d && !d.available ? "full" : ""}`}>
      <span className="lab-queue-dot" />
      <strong>{t("dash.lab_queue", { active: d?.active ?? "—", max: d?.max ?? "—" })}</strong>
      {d && d.available ? (
        <em>{t("dash.lab_queue_free")}</em>
      ) : d && d.next_free_in_min != null ? (
        <em>{t("dash.lab_queue_wait", { min: d.next_free_in_min })}</em>
      ) : null}
    </div>
  );
}
/**
 * «Моя лаборатория» — большая кнопка запуска персональной комнаты.
 * Статус: нет лабы / STOPPED → «Запустить лабораторию»;
 * RUNNING → «Войти в комнату» (fullscreen, /lab/:runId).
 */
export function PersonalLabCard({ sessions }: { sessions?: Session[] }) {
  const t = useT();
  const router = useRouter();
  const auth = useAuth();
  const { data: me } = useApi<{ lab_os?: string }>(auth.user ? "/users/me" : null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [os, setOs] = useState(me?.lab_os ?? "kali");
  useEffect(() => {
    if (me?.lab_os) setOs(me.lab_os);
  }, [me?.lab_os]);
  if (!auth.user) return null;
  const kalis = sessions?.filter((s) => s.scenario_id === "personal-kali") ?? [];
  // После рестартов/очисток может остаться несколько сессий — берём живую, не зомби.
  const lab =
    (kalis.find((s) => s.lab?.status === "RUNNING") ?? kalis[0])?.lab;
  const running = lab?.status === "RUNNING";
  // Живой TTL-отсчёт (паттерн ops-дашбордов: сессия «дышит»)
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!running || !lab?.expires_at) return;
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, [running, lab?.expires_at]);
  const ttlLeft =
    running && lab?.expires_at
      ? Math.max(0, Math.round((new Date(lab.expires_at).getTime() - now) / 60_000))
      : null;

  async function enterOrStart() {
    setBusy(true);
    setError(null);
    try {
      if (lab && running) {
        void document.documentElement.requestFullscreen?.().catch(() => {});
        router.push(`/lab/${encodeURIComponent(lab.id)}`);
        return;
      }
      const fetchLab = async (): Promise<Session["lab"]> => {
        // Идемпотентно: живую/остановленную сессию вернёт, зомби — пересоздаст
        // (для LAB-сценариев сразу createLab + startLab).
        const res = await post<StartSessionResponse>("/sessions", {
          scenario_id: "personal-kali",
        });
        const sess = await api<Session>(
          `/sessions/${encodeURIComponent(res.session_id)}`,
        );
        return sess.lab;
      };
      let l = await fetchLab();
      // Страховка: если всё же вернулась сессия с уничтоженной лабой — просим новую.
      if (l && l.status === "DESTROYED") l = await fetchLab();
      if (
        l &&
        ["STOPPED", "ERROR", "CREATING", "READY", "STOPPING"].includes(
          l.status,
        )
      ) {
        try {
          await post(`/labs/${encodeURIComponent(l.id)}/start`);
        } catch {
          /* комната сама дождётся RUNNING */
        }
      }
      if (l) {
        // Кнопка = user gesture: входим в браузерный fullscreen сразу,
        // комната сама дождётся готовности среды.
        void document.documentElement.requestFullscreen?.().catch(() => {});
        router.push(`/lab/${encodeURIComponent(l.id)}`);
      }
    } catch (e) {
      setError((e as Error).message || t("common.error"));
    } finally {
      setBusy(false);
    }
  }

  async function pickOs(v: string) {
    setOs(v);
    try {
      await post("/users/me/lab-os", { os: v });
    } catch {
      /* ignore */
    }
  }

  return (
    <section className="lab-launch" aria-label={t("room.mine")}>
      <div className="lab-launch-info">
        <div className="lab-launch-icon">
          <FlaskConical size={26} />
        </div>
        <div>
          <h3>
            <span className="lab-live-dot" aria-hidden="true" />
            {t("room.mine")}
          </h3>
          <p className="muted">
            {running
              ? t("room.running")
              : t("room.not_running")}
          </p>
          {running && ttlLeft != null && (
            <span className="lab-ttl">
              <Clock3 size={13} />
              {t("lab.ttl_left", { t: hoursLabel(ttlLeft) })}
            </span>
          )}
          {error && <p className="lab-launch-error">{error}</p>}
        </div>
      </div>
      <div className="lab-launch-actions">
        <label className="lab-launch-os">
          <span>{t("room.os")}</span>
          <select value={os} onChange={(e) => void pickOs(e.target.value)} disabled={busy}>
            <option value="kali">Kali Linux</option>
            <option value="parrot">Parrot OS</option>
            <option value="ubuntu">Ubuntu 24.04</option>
          </select>
        </label>
        <button
          className="lab-launch-btn"
          onClick={() => void enterOrStart()}
          disabled={busy}
        >
          {busy ? <Loader2 size={19} className="spin" /> : running ? <Play size={19} /> : <FlaskConical size={19} />}
          {busy
            ? t("room.starting")
            : running
              ? t("room.enter")
              : t("room.start")}
        </button>
      </div>
    </section>
  );
}
export function Dashboard() {
  const auth = useAuth();
  const t = useT();
  const request = useApi<DashboardData>(
    auth.user ? "/users/me/dashboard" : null,
  );
  const stats = request.data?.analytics;
  return (
    <>
      <PageHeading
        eyebrow={t("dash.eyebrow")}
        title={
          auth.user
            ? t("dash.welcome", { name: auth.user.name.split(" ")[0] })
            : t("dash.next_step")
        }
        description={t("dash.desc")}
      />
      <section className="welcome-banner">
        <div>
          <span className="banner-label">
            <span />
            {t("dash.banner_label")}
          </span>
          <h2>
            {t("dash.banner_h1")}
            <br />
            <span>{t("dash.banner_h2")}</span>
          </h2>
          <p>{t("dash.banner_p")}</p>
          <div className="banner-command">
            <span className="banner-prompt" aria-hidden="true">
              <span className="banner-prompt-user">operator</span>
              <span className="banner-prompt-path">@cyberalem</span>
              <span className="banner-prompt-cwd">:~</span>$
              <span className="banner-cursor" aria-hidden="true" />
            </span>
            <div className="banner-actions">
              <Link className="button mint" href="/directions">
                {t("nav.choose_direction")}
                <ArrowRight size={17} />
              </Link>
              <Link className="button range-button" href="/cyber-range">
                <FlaskConical size={17} />
                {t("nav.cyber_range")}
                <ArrowRight size={17} />
              </Link>
            </div>
          </div>
        </div>
        <div className="banner-mark" aria-hidden="true">
          <div className="reticle">
            <span className="ret-ring r1" />
            <span className="ret-ring r2" />
            <span className="ret-ring r3" />
            <span className="ret-cross h" />
            <span className="ret-cross v" />
            <span className="ret-core" />
            <span className="ret-tick tl" />
            <span className="ret-tick tr" />
            <span className="ret-tick bl" />
            <span className="ret-tick br" />
            <span className="ret-target">TARGET</span>
          </div>
          <span className="mark-label label-one">LEARN</span>
          <span className="mark-label label-two">PRACTICE</span>
          <span className="mark-label label-three">PROTECT</span>
        </div>
      </section>
      {!auth.user && <PublicStats />}
      {!auth.user && <FeaturesGrid />}
      {!auth.user && <OnboardCard />}
      {auth.user && (
        <div className="stats-grid">
        <Stat
          label={t("dash.stat_completed")}
          value={stats?.completed ?? "—"}
          note={t("dash.stat_completed_note")}
          icon={<BookOpen size={19} />}
        />
        <Stat
          label={t("dash.stat_avg")}
          value={stats ? `${stats.average_score}%` : "—"}
          note={t("dash.stat_avg_note")}
          icon={<TrendingUp size={19} />}
        />
        <Stat
          label={t("dash.stat_time")}
          value={stats ? minutes(stats.time_spent_minutes) : "—"}
          note={t("dash.stat_time_note")}
          icon={<Clock3 size={19} />}
        />
        <Stat
          label={t("dash.stat_completion")}
          value={stats ? `${stats.completion_rate}%` : "—"}
          note={t("dash.stat_completion_note")}
          icon={<Flag size={19} />}
        />
      </div>
      )}
      {auth.user && <CyberScoreCard />}
      <PersonalLabCard sessions={request.data?.active_sessions} />
      <LabQueueCard />
      <section className="section">
        <SectionTitle
          title={t("dash.directions_title")}
          href="/directions"
          link={t("dash.all_directions")}
        />
        <DirectionCards />
      </section>
      {request.data?.active_sessions.length ? (
        <section className="section">
          <SectionTitle title={t("dash.continue")} />
          <div className="stack">
            {request.data.active_sessions.map((s) => (
              <div className="panel active-session" key={s.id}>
                <div>
                  <h3>{s.scenario_title}</h3>
                  <Progress value={s.progress} label={t("dash.done")} />
                </div>
                <Link
                  className="button primary"
                  href={`/practice/${encodeURIComponent(s.id)}/workspace`}
                >
                  {t("dash.continue_btn")}
                  <ArrowRight size={16} />
                </Link>
              </div>
            ))}
          </div>
        </section>
      ) : null}
      <section className="panel learning-banner section">
        <span className="small-icon">
          <Sparkles size={21} />
        </span>
        <div>
          <h2>
            {t("dash.learning_h1")}
            <br />
            {t("dash.learning_h2")}
          </h2>
          <p>{t("dash.learning_p")}</p>
        </div>
        <Link href="/practices" className="text-link">
          {t("dash.find_practice")}
          <ArrowRight size={17} />
        </Link>
        <div className="learning-note">
          <CheckCheck size={17} />
          {t("dash.safe_env")}
        </div>
      </section>
      <div className="dashboard-bottom">
        <section className="panel">
          <SectionTitle
            title={t("dash.recent")}
            href="/history"
            link={t("dash.history")}
          />
          {auth.user ? (
            <DataState {...request} retry={request.reload}>
              <HistoryTable items={request.data?.recent_history ?? []} />
            </DataState>
          ) : (
            <Empty
              title={t("dash.results_here")}
              text={t("dash.results_here_text")}
              action={
                <Link href="/login" className="text-link">
                  {t("auth.login_btn")}
                  <ArrowRight size={16} />
                </Link>
              }
            />
          )}
        </section>
      </div>
      {request.data?.recommendations.length ? (
        <section className="section">
          <SectionTitle title={t("dash.recommend")} />
          <div className="scenario-grid">
            {request.data.recommendations.map((s) => (
              <ScenarioCard key={s.id} scenario={s} />
            ))}
          </div>
        </section>
      ) : null}
    </>
  );
}
export function AnalyticsView({ data }: { data: Analytics }) {
  const t = useT();
  return (
    <>
      <div className="stats-grid">
        <Stat label={t("progress.avg")} value={`${data.average_score}%`} />
        <Stat label={t("progress.total")} value={data.practice_count} />
        <Stat label={t("progress.completed")} value={`${data.completion_rate}%`} />
        <Stat label={t("progress.avg_time")} value={minutes(data.average_time_minutes)} />
      </div>
      <div className="two-columns">
        <section className="panel">
          <h2>{t("progress.by_direction")}</h2>
          <div className="stack">
            {data.directions.length ? (
              data.directions.map((d) => (
                <div key={d.direction}>
                  <Progress value={d.percent} label={d.name} />
                  <small className="muted">
                    {d.completed} {t("progress.of", { total: d.total })}
                  </small>
                </div>
              ))
            ) : (
              <Empty title={t("progress.no_progress")} />
            )}
          </div>
        </section>
        <section className="panel">
          <h2>{t("progress.hard_tasks")}</h2>
          {data.difficult_tasks.length ? (
            <div className="stack">
              {data.difficult_tasks.map((t, i) => (
                <Progress key={i} value={t.success_rate} label={t.title} />
              ))}
            </div>
          ) : (
            <Empty title="Пока нет выполненных заданий" />
          )}
        </section>
      </div>
      <div className="stats-grid">
        <Stat label={t("progress.errors")} value={data.errors} />
        <Stat label={t("progress.hints")} value={data.hints} />
        <Stat label={t("progress.failed")} value={data.failed} />
        <Stat label={t("progress.total_time")} value={minutes(data.time_spent_minutes)} />
      </div>
      <div className="two-columns">
        <section className="panel">
          <h2>{t("progress.strengths")}</h2>
          {data.strengths.length ? (
            <ul className="plain-list">
              {data.strengths.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
          ) : (
            <p className="muted">{t("progress.strengths_empty")}</p>
          )}
        </section>
        <section className="panel">
          <h2>{t("progress.weaknesses")}</h2>
          {data.weaknesses.length ? (
            <ul className="plain-list">
              {data.weaknesses.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
          ) : (
            <p className="muted">{t("progress.weaknesses_empty")}</p>
          )}
        </section>
      </div>
    </>
  );
}
function ProgressContent() {
  const request = useApi<Analytics>("/users/me/progress");
  return (
    <DataState {...request} retry={request.reload}>
      {request.data && <AnalyticsView data={request.data} />}
    </DataState>
  );
}
export function ProgressPage() {
  const t = useT();
  return (
    <>
      <PageHeading
        eyebrow={t("progress.eyebrow")}
        title={t("progress.title")}
        description={t("progress.desc")}
      />
      <RequireAuth>
        <ProgressContent />
      </RequireAuth>
    </>
  );
}
function HistoryContent() {
  const [page, setPage] = useState(1);
  const request = useApi<PageData<HistoryItem>>(
    `/users/me/history?page=${page}&page_size=20`,
  );
  return (
    <DataState {...request} retry={request.reload}>
      <section className="panel">
        <HistoryTable items={request.data?.items ?? []} />
      </section>
      {request.data && (
        <Pagination
          page={page}
          total={request.data.total}
          size={20}
          onChange={setPage}
        />
      )}
    </DataState>
  );
}
export function HistoryPage() {
  const t = useT();
  return (
    <>
      <PageHeading
        eyebrow={t("history.eyebrow")}
        title={t("history.title")}
        description={t("history.desc")}
      />
      <RequireAuth>
        <HistoryContent />
      </RequireAuth>
    </>
  );
}
interface PublicStats {
  students: number;
  teachers: number;
  practices: number;
  courses: number;
  sessions_done: number;
  labs_running: number;
}

function OnboardCard() {
  const t = useT();
  const steps = [
    {
      icon: <Compass size={20} />,
      title: t("onboard.s1_t"),
      text: t("onboard.s1_d"),
      href: "/directions",
    },
    {
      icon: <BookOpen size={20} />,
      title: t("onboard.s2_t"),
      text: t("onboard.s2_d"),
      href: "/practices",
    },
    {
      icon: <FlaskConical size={20} />,
      title: t("onboard.s3_t"),
      text: t("onboard.s3_d"),
      href: "/cyber-range",
    },
  ];
  return (
    <section className="section">
      <SectionTitle title={t("onboard.title")} />
      <p className="onboard-sub">{t("onboard.p")}</p>
      <div className="onboard-grid">
        {steps.map((s, i) => (
          <Link key={s.title} className="onboard-card" href={s.href}>
            <span className="onboard-num" aria-hidden="true">
              {i + 1}
            </span>
            <span className="onboard-icon">{s.icon}</span>
            <h3>{s.title}</h3>
            <p>{s.text}</p>
          </Link>
        ))}
      </div>
    </section>
  );
}
function PublicStats() {
  const t = useT();
  const req = useApi<PublicStats>("/public/stats");
  const d = req.data;
  return (
    <section className="section">
      <SectionTitle title={t("public.platform_title")} />
      <div className="stats-grid">
        <Stat
          label={t("public.stats_students")}
          value={d ? String(d.students) : "—"}
          note={d ? `${d.teachers} ${t("public.stats_teachers")}` : ""}
          icon={<UsersRound size={19} />}
        />
        <Stat
          label={t("public.stats_practices")}
          value={d ? String(d.practices) : "—"}
          note={d ? `${d.courses} ${t("public.stats_courses")}` : ""}
          icon={<BookOpen size={19} />}
        />
        <Stat
          label={t("public.stats_labs")}
          value={d ? String(d.labs_running) : "—"}
          note={t("public.stats_labs_note")}
          icon={<FlaskConical size={19} />}
        />
        <Stat
          label={t("public.stats_done")}
          value={d ? String(d.sessions_done) : "—"}
          note={t("public.stats_done_note")}
          icon={<Flag size={19} />}
        />
      </div>
    </section>
  );
}

function FeaturesGrid() {
  const t = useT();
  const feats = [
    {
      icon: <BookOpen size={20} />,
      title: t("feat.courses_t"),
      text: t("feat.courses_d"),
      href: "/directions",
    },
    {
      icon: <MonitorSmartphone size={20} />,
      title: t("feat.labs_t"),
      text: t("feat.labs_d"),
      href: "/practices",
    },
    {
      icon: <Sparkles size={20} />,
      title: t("feat.ai_t"),
      text: t("feat.ai_d"),
      href: "/ai-agent",
    },
    {
      icon: <Gauge size={20} />,
      title: t("feat.arena_t"),
      text: t("feat.arena_d"),
      href: "/arena",
    },
    {
      icon: <TrendingUp size={20} />,
      title: t("feat.leader_t"),
      text: t("feat.leader_d"),
      href: "/leaderboard",
    },
    {
      icon: <ShieldCheck size={20} />,
      title: t("feat.citizen_t"),
      text: t("feat.citizen_d"),
      href: "/citizen",
    },
  ];
  return (
    <section className="section">
      <SectionTitle title={t("feat.title")} />
      <div className="features-grid">
        {feats.map((f) => (
          <Link key={f.title} className="feature-card" href={f.href}>
            <span className="feature-icon">{f.icon}</span>
            <h3>{f.title}</h3>
            <p>{f.text}</p>
            <span className="feature-arrow">
              {t("feat.more")} <ArrowRight size={15} />
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}

const OS_LABELS_PROFILE: Record<string, string> = {
  kali: "Kali Linux",
  parrot: "Parrot OS",
  ubuntu: "Ubuntu 24.04",
};

export function ProfilePage() {
  const auth = useAuth();
  const { t, lang, setLang } = useI18n();
  const dash = useApi<DashboardData>(auth.user ? "/users/me/dashboard" : null);
  const hist = useApi<PageData<HistoryItem>>(auth.user ? "/users/me/history" : null);
  const [name, setName] = useState(auth.user?.name ?? "");
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [nameBusy, setNameBusy] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);
  const [nameMsg, setNameMsg] = useState<{ ok: boolean; text: string }>();
  const [pwMsg, setPwMsg] = useState<{ ok: boolean; text: string }>();
  const [allBusy, setAllBusy] = useState(false);
  const [allMsg, setAllMsg] = useState<{ ok: boolean; text: string }>();
  const [os, setOs] = useState(auth.user?.lab_os ?? "kali");
  const [osMsg, setOsMsg] = useState<{ ok: boolean; text: string }>();
  const certs = useApi<{
    certificates: {
      id: string;
      url: string;
      scenario_title: string;
      score: number;
      status: string;
      grade: "excellent" | "passed";
      finished_at: string;
    }[];
  }>(auth.user ? "/users/me/certificates" : null);

  useEffect(() => {
    if (auth.user?.lab_os) setOs(auth.user.lab_os);
  }, [auth.user?.lab_os]);

  async function saveName() {
    setNameBusy(true);
    setNameMsg(undefined);
    try {
      await api<{ name: string }>("/users/me", {
        method: "PATCH",
        body: JSON.stringify({ name: name.trim() }),
      });
      await auth.refreshProfile();
      setNameMsg({ ok: true, text: t("profile.name_saved") });
    } catch (e) {
      setNameMsg({ ok: false, text: (e as Error).message });
    } finally {
      setNameBusy(false);
    }
  }

  async function changePassword() {
    setPwBusy(true);
    setPwMsg(undefined);
    try {
      await api("/users/me/password", {
        method: "POST",
        body: JSON.stringify({
          current_password: curPw,
          new_password: newPw,
        }),
      });
      setCurPw("");
      setNewPw("");
      setConfirmPw("");
      setPwMsg({ ok: true, text: t("profile.pw_changed") });
    } catch (e) {
      setPwMsg({ ok: false, text: (e as Error).message });
    } finally {
      setPwBusy(false);
    }
  }

  async function saveOs(v: string) {
    setOs(v);
    setOsMsg(undefined);
    try {
      await api("/users/me/lab-os", {
        method: "POST",
        body: JSON.stringify({ os: v }),
      });
      setOsMsg({ ok: true, text: t("profile.os_saved") });
    } catch (e) {
      setOsMsg({ ok: false, text: (e as Error).message });
    }
  }

  async function logoutAll() {
    if (!window.confirm(t("profile.logout_all_warn"))) return;
    setAllBusy(true);
    setAllMsg(undefined);
    try {
      await api("/users/me/logout-all", { method: "POST" });
      setAllMsg({ ok: true, text: t("profile.logout_all_done") });
      setTimeout(() => auth.logout(), 1200);
    } catch (e) {
      setAllMsg({ ok: false, text: (e as Error).message });
    } finally {
      setAllBusy(false);
    }
  }

  const stats = dash.data?.analytics;
  const active = dash.data?.active_sessions ?? [];

  return (
    <>
      <PageHeading
        title={t("profile.title")}
        description={t("profile.title_desc")}
      />
      <RequireAuth>
        {auth.user && (
          <>
            <section className="panel account-hero">
              <span className="avatar large">{auth.user.name.charAt(0)}</span>
              <div className="account-hero-id">
                <h2>{auth.user.name}</h2>
                <p>{auth.user.email}</p>
                <div className="account-hero-meta">
                  <Badge value={auth.user.role} />
                  <span>
                    {t("profile.registered")}: {date(auth.user.registered_at)}
                  </span>
                  <span>{auth.user.group ?? t("profile.no_group")}</span>
                </div>
              </div>
              <div className="account-hero-stats">
                <div>
                  <b>{stats?.completed ?? auth.user.completed ?? 0}</b>
                  <span>{t("dash.stat_completed")}</span>
                </div>
                <div>
                  <b>{stats ? `${stats.average_score}%` : "—"}</b>
                  <span>{t("dash.stat_avg")}</span>
                </div>
                <div>
                  <b>
                    {stats ? minutes(stats.time_spent_minutes) : "—"}
                  </b>
                  <span>{t("dash.stat_time")}</span>
                </div>
              </div>
            </section>

            <div className="account-grid">
              <section className="panel">
                <h3 className="account-card-title">
                  <FlaskConical size={16} /> {t("profile.active_title")}
                </h3>
                {active.length ? (
                  <div className="stack">
                    {active.map((s) => (
                      <div className="account-active" key={s.id}>
                        <div>
                          <strong>{s.scenario_title}</strong>
                          <Progress value={s.progress} label={t("dash.done")} />
                          {s.lab ? (
                            <p className="muted small">
                              {t("profile.lab_status")}: {s.lab.status}
                              {s.lab.os ? (
                                <> · {OS_LABELS_PROFILE[s.lab.os] ?? s.lab.os}</>
                              ) : null}
                              {s.lab.remaining_minutes ? (
                                <>
                                  {" "}
                                  · {t("profile.remain")}{" "}
                                  {s.lab.remaining_minutes}
                                  {t("profile.remain_min")}
                                </>
                              ) : null}
                            </p>
                          ) : null}
                        </div>
                        <Link
                          className="button primary"
                          href={`/practice/${encodeURIComponent(s.id)}/workspace`}
                        >
                          {t("profile.open_ws")}
                          <ArrowRight size={16} />
                        </Link>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Empty
                    title={t("profile.active_empty")}
                    text={t("profile.active_empty_text")}
                    action={
                      <Link href="/practices" className="text-link">
                        {t("nav.practices")}
                        <ArrowRight size={16} />
                      </Link>
                    }
                  />
                )}
              </section>

              <section className="panel">
                <h3 className="account-card-title">
                  <TrendingUp size={16} /> {t("profile.stats_title")}
                </h3>
                <div className="account-stats">
                  <div>
                    <b>{stats?.completion_rate ?? "—"}</b>
                    <span>%</span>
                  </div>
                  <div className="account-stats-label">
                    {t("dash.stat_completion")}
                  </div>
                  {(stats?.directions ?? []).length ? (
                    (stats?.directions ?? []).map((d) => (
                      <div key={d.direction} className="account-dir">
                        <span>{d.name}</span>
                        <Progress value={d.percent} label={`${d.completed}/${d.total}`} />
                      </div>
                    ))
                  ) : (
                    <p className="muted small">{t("profile.stats_empty")}</p>
                  )}
                </div>
              </section>

              <section className="panel">
                <h3 className="account-card-title">
                  <Languages size={16} /> {t("profile.prefs_title")}
                </h3>
                <div className="settings-block">
                  <label className="field-label" htmlFor="pf-name">
                    {t("profile.name_label")}
                  </label>
                  <div className="inline settings-row">
                    <input
                      id="pf-name"
                      className="input"
                      value={name}
                      maxLength={80}
                      onChange={(e) => setName(e.target.value)}
                    />
                    <Button busy={nameBusy} onClick={saveName}>
                      {t("profile.save")}
                    </Button>
                  </div>
                  {nameMsg && (
                    <p className={nameMsg.ok ? "form-ok" : "form-error"}>
                      {nameMsg.text}
                    </p>
                  )}
                </div>
                <div className="settings-block">
                  <label className="field-label">
                    {t("profile.lang_label")}
                  </label>
                  <div className="inline settings-row">
                    <select
                      className="input"
                      value={lang}
                      onChange={(e) =>
                        setLang(e.target.value as "kk" | "ru" | "en" | "ko")
                      }
                    >
                      <option value="kk">Қазақша</option>
                      <option value="ru">Русский</option>
                      <option value="en">English</option>
                      <option value="ko">한국어</option>
                    </select>
                  </div>
                </div>
                <div className="settings-block">
                  <label className="field-label">
                    <MonitorSmartphone size={13} /> {t("profile.labos_label")}
                  </label>
                  <div className="inline settings-row">
                    <select
                      className="input"
                      value={os}
                      onChange={(e) => void saveOs(e.target.value)}
                    >
                      <option value="kali">Kali Linux</option>
                      <option value="parrot">Parrot OS</option>
                      <option value="ubuntu">Ubuntu 24.04</option>
                    </select>
                  </div>
                  {osMsg && (
                    <p className={osMsg.ok ? "form-ok" : "form-error"}>
                      {osMsg.text}
                    </p>
                  )}
                </div>
              </section>

              <section className="panel">
                <h3 className="account-card-title">
                  <KeyRound size={16} /> {t("profile.security_title")}
                </h3>
                <div className="settings-block">
                  <label className="field-label">{t("profile.pw_label")}</label>
                  <input
                    className="input"
                    type="password"
                    value={curPw}
                    autoComplete="current-password"
                    placeholder={t("profile.pw_current")}
                    onChange={(e) => setCurPw(e.target.value)}
                  />
                  <input
                    className="input"
                    type="password"
                    value={newPw}
                    autoComplete="new-password"
                    placeholder={t("profile.pw_new")}
                    onChange={(e) => setNewPw(e.target.value)}
                  />
                  <input
                    className="input"
                    type="password"
                    value={confirmPw}
                    autoComplete="new-password"
                    placeholder={t("profile.pw_confirm")}
                    onChange={(e) => setConfirmPw(e.target.value)}
                  />
                  <Button
                    variant="secondary"
                    busy={pwBusy}
                    onClick={() => {
                      if (newPw !== confirmPw) {
                        setPwMsg({
                          ok: false,
                          text: t("profile.pw_mismatch"),
                        });
                        return;
                      }
                      void changePassword();
                    }}
                  >
                    {t("profile.change_pw")}
                  </Button>
                  {pwMsg && (
                    <p className={pwMsg.ok ? "form-ok" : "form-error"}>
                      {pwMsg.text}
                    </p>
                  )}
                </div>
                <div className="settings-block">
                  <label className="field-label">
                    {t("profile.sessions_title")}
                  </label>
                  <p className="muted small">{t("profile.logout_all_warn")}</p>
                  <Button
                    variant="secondary"
                    busy={allBusy}
                    onClick={() => void logoutAll()}
                  >
                    <LogOut size={15} /> {t("profile.logout_all")}
                  </Button>
                  {allMsg && (
                    <p className={allMsg.ok ? "form-ok" : "form-error"}>
                      {allMsg.text}
                    </p>
                  )}
                </div>
              </section>

              <section className="panel account-history">
                <h3 className="account-card-title">
                  <Clock3 size={16} /> {t("profile.history_title")}
                </h3>
                {auth.user ? (
                  <DataState {...hist} retry={hist.reload}>
                    <HistoryTable
                      items={(hist.data?.items ?? []).slice(0, 8)}
                    />
                  </DataState>
                ) : null}
              </section>

              <section className="panel">
                <h3 className="account-card-title">
                  <BadgeCheck size={16} /> {t("profile.certs_title")}
                </h3>
                {auth.user && (
                  <DataState {...certs} retry={certs.reload}>
                    {certs.data &&
                    certs.data.certificates.length ? (
                      <div className="stack">
                        {certs.data.certificates.map((c) => (
                          <Link
                            key={c.id}
                            className="cert-row"
                            href={c.url}
                            title="Открыть сертификат (печать/верификация)"
                          >
                            <span className="cert-row-icon">
                              <BadgeCheck size={17} />
                            </span>
                            <span className="cert-row-body">
                              <strong>{c.scenario_title}</strong>
                              <small>
                                {c.id} · {date(c.finished_at)} · {c.score}/100
                                {c.grade === "excellent"
                                  ? ` · ${t("cert.excellent")}`
                                  : ""}
                              </small>
                            </span>
                            <ArrowRight size={15} className="muted" />
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <p className="muted small">{t("profile.certs_empty")}</p>
                    )}
                  </DataState>
                )}
              </section>
            </div>
          </>
        )}
      </RequireAuth>
    </>
  );
}
